import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { get } from "../../../utils/HttpRequest";
import config from "../../../config/config.json";
import Header from "../../Header/Header";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { isHtmlEncoded, decodeHtmlEntities } from "../../../utils/CommonTopicMethods";
import "./AttemptHistory.css";

const AttemptHistory = () => {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const lessonid = params.get("lessonid");
  const email = params.get("email");
  const username = params.get("username") || email;

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expandedAttempts, setExpandedAttempts] = useState({});

  useEffect(() => {
    if (!lessonid || !email) return;
    setLoading(true);
    get(
      config.api.url +
        "getLessonsCompletionDetailsOneUser/" +
        email.replace(/ /g, "+") +
        "?attemptHistory=true&lessonid=" +
        lessonid
    )
      .then((result) => {
        setData(result.data);
        // Auto-expand the latest attempt
        if (result.data && result.data.attempts && result.data.attempts.length > 0) {
          setExpandedAttempts({ 0: true });
        }
        setLoading(false);
      })
      .catch((err) => {
        console.log("Error fetching attempt history", err);
        setLoading(false);
      });
  }, [lessonid, email]);

  const toggleAttempt = (index) => {
    setExpandedAttempts((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const getPassCount = () => {
    if (!data || !data.attempts) return 0;
    return data.attempts.filter((a) => a.assessmentResult === "Pass").length;
  };

  const getBestScore = () => {
    if (!data || !data.attempts) return "—";
    const scores = data.attempts
      .map((a) => a.assessmentScore)
      .filter((s) => s != null);
    return scores.length > 0 ? Math.max(...scores) + "%" : "—";
  };

  const decodeQuestion = (q) => {
    if (!q) return "";
    let decoded = q;
    if (isHtmlEncoded(decoded)) {
      decoded = decodeURIComponent(decoded);
    }
    decoded = decodeHtmlEntities(decoded);
    return decoded;
  };

  return (
    <div className="ah-page app">
      <Header />
      <div className="ah-content">
        {/* Header */}
        <div className="ah-header">
          <div className="ah-header-left">
            <h4>Attempt History</h4>
            <p>{username} — Lesson: {lessonid}</p>
          </div>
          <button
            className="ah-back-btn"
            onClick={() => navigate(-1)}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Back to Report
          </button>
        </div>

        {loading ? (
          <div>
            {/* Skeleton Summary */}
            <div className="ah-summary">
              {Array.from({ length: 4 }).map((_, i) => (
                <div className="ah-stat" key={i}>
                  <div className="skeleton-line" style={{ width: "40px", height: "28px", marginBottom: "6px", borderRadius: "4px" }}></div>
                  <div className="skeleton-line" style={{ width: "80px", height: "12px", borderRadius: "4px" }}></div>
                </div>
              ))}
            </div>

            {/* Skeleton Timeline */}
            <div className="ah-timeline">
              {/* First attempt - expanded with answer rows */}
              <div className="ah-attempt">
                <div className="ah-attempt-dot" style={{ background: "#e9ecef", boxShadow: "0 0 0 2px #e9ecef" }}></div>
                <div className="ah-attempt-card">
                  <div className="ah-attempt-header" style={{ cursor: "default" }}>
                    <div className="ah-attempt-header-left" style={{ gap: "1rem" }}>
                      <div className="skeleton-line" style={{ width: "90px", height: "16px", borderRadius: "4px" }}></div>
                      <div className="skeleton-line" style={{ width: "110px", height: "14px", borderRadius: "4px" }}></div>
                    </div>
                    <div className="ah-attempt-header-right" style={{ gap: "0.5rem" }}>
                      <div className="skeleton-line" style={{ width: "50px", height: "22px", borderRadius: "20px" }}></div>
                      <div className="skeleton-line" style={{ width: "45px", height: "22px", borderRadius: "20px" }}></div>
                      <div className="skeleton-line" style={{ width: "55px", height: "22px", borderRadius: "20px" }}></div>
                    </div>
                  </div>
                  <div style={{ borderTop: "1px solid #f0f0f0", padding: "1rem 1.25rem" }}>
                    {Array.from({ length: 4 }).map((_, j) => (
                      <div key={j} style={{ display: "flex", gap: "1rem", marginBottom: "0.75rem", alignItems: "center" }}>
                        <div className="skeleton-line" style={{ width: "20px", height: "14px", borderRadius: "4px" }}></div>
                        <div className="skeleton-line" style={{ flex: 1, height: "14px", borderRadius: "4px" }}></div>
                        <div className="skeleton-line" style={{ width: "80px", height: "14px", borderRadius: "4px" }}></div>
                        <div className="skeleton-line" style={{ width: "80px", height: "14px", borderRadius: "4px" }}></div>
                        <div className="skeleton-line" style={{ width: "20px", height: "14px", borderRadius: "4px" }}></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Remaining attempts - collapsed (header only) */}
              {Array.from({ length: 2 }).map((_, i) => (
                <div className="ah-attempt" key={i}>
                  <div className="ah-attempt-dot" style={{ background: "#e9ecef", boxShadow: "0 0 0 2px #e9ecef" }}></div>
                  <div className="ah-attempt-card">
                    <div className="ah-attempt-header" style={{ cursor: "default" }}>
                      <div className="ah-attempt-header-left" style={{ gap: "1rem" }}>
                        <div className="skeleton-line" style={{ width: "90px", height: "16px", borderRadius: "4px" }}></div>
                        <div className="skeleton-line" style={{ width: "110px", height: "14px", borderRadius: "4px" }}></div>
                      </div>
                      <div className="ah-attempt-header-right" style={{ gap: "0.5rem" }}>
                        <div className="skeleton-line" style={{ width: "50px", height: "22px", borderRadius: "20px" }}></div>
                        <div className="skeleton-line" style={{ width: "45px", height: "22px", borderRadius: "20px" }}></div>
                        <div className="skeleton-line" style={{ width: "55px", height: "22px", borderRadius: "20px" }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : !data || !data.attempts || data.attempts.length === 0 ? (
          <div className="ah-empty">
            <p>No attempt history found for this user.</p>
          </div>
        ) : (
          <>
            {/* Summary */}
            <div className="ah-summary">
              <div className="ah-stat">
                <span className="ah-stat-value">{data.totalAttempts}</span>
                <span className="ah-stat-label">Total Attempts</span>
              </div>
              <div className="ah-stat">
                <span className="ah-stat-value" style={{ color: "#28a745" }}>
                  {getPassCount()}
                </span>
                <span className="ah-stat-label">Passed</span>
              </div>
              <div className="ah-stat">
                <span className="ah-stat-value" style={{ color: "#dc3545" }}>
                  {data.totalAttempts - getPassCount()}
                </span>
                <span className="ah-stat-label">Failed</span>
              </div>
              <div className="ah-stat">
                <span className="ah-stat-value" style={{ color: "#0d6efd" }}>
                  {getBestScore()}
                </span>
                <span className="ah-stat-label">Best Score</span>
              </div>
            </div>

            {/* Timeline */}
            <div className="ah-timeline">
              {data.attempts.map((attempt, idx) => (
                <div className="ah-attempt" key={idx}>
                  <div
                    className={`ah-attempt-dot ${
                      attempt.assessmentResult === "Pass" ? "pass" : "fail"
                    }`}
                  ></div>
                  <div className="ah-attempt-card">
                    <div
                      className="ah-attempt-header"
                      onClick={() => toggleAttempt(idx)}
                    >
                      <div className="ah-attempt-header-left">
                        <span className="ah-attempt-number">
                          Attempt {attempt.attemptNumber}
                        </span>
                        <span className="ah-attempt-date">
                          {attempt.completionDate}
                        </span>
                      </div>
                      <div className="ah-attempt-header-right">
                        <span
                          className={`ah-badge ${
                            attempt.assessmentResult === "Pass"
                              ? "pass"
                              : "fail"
                          }`}
                        >
                          {attempt.assessmentResult || "N/A"}
                        </span>
                        {attempt.assessmentScore != null && (
                          <span className="ah-badge score">
                            {attempt.assessmentScore}%
                          </span>
                        )}
                        <span className="ah-badge lang">
                          {attempt.language}
                        </span>
                        <FontAwesomeIcon
                          icon={faChevronDown}
                          className={`ah-expand-icon ${
                            expandedAttempts[idx] ? "open" : ""
                          }`}
                        />
                      </div>
                    </div>

                    {expandedAttempts[idx] && (
                      <div className="ah-answers">
                        {attempt.answers && attempt.answers.length > 0 ? (
                          <table className="ah-answer-table">
                            <thead>
                              <tr>
                                <th style={{ width: "5%" }}>#</th>
                                <th style={{ width: "40%" }}>Question</th>
                                <th style={{ width: "22%" }}>User's Answer</th>
                                <th style={{ width: "22%" }}>Correct Answer</th>
                                <th style={{ width: "11%" }}>Result</th>
                              </tr>
                            </thead>
                            <tbody>
                              {attempt.answers.map((answer, aIdx) => (
                                <tr
                                  key={aIdx}
                                  className={
                                    answer.result === 1
                                      ? "correct"
                                      : "incorrect"
                                  }
                                >
                                  <td>{aIdx + 1}</td>
                                  <td
                                    dangerouslySetInnerHTML={{
                                      __html: decodeQuestion(answer.question),
                                    }}
                                  ></td>
                                  <td>{answer.answerByUser}</td>
                                  <td>{answer.correctAnswer}</td>
                                  <td>
                                    <span
                                      className={`ah-result-icon ${
                                        answer.result === 1
                                          ? "correct"
                                          : "incorrect"
                                      }`}
                                    >
                                      {answer.result === 1 ? "✓" : "✗"}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <p className="text-muted mb-0">
                            No answer details available for this attempt.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AttemptHistory;
